import { polygon, Feature, Polygon, Properties, Position, lineString, LineString, Point } from '@turf/helpers';
import lineArc from '@turf/line-arc';
import * as geolib from 'geolib';

import { LightningCircle } from '@mono/types/src/platmap';
import { LightningFeatureProperties, Strikes } from '@mono/lightning-context';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';
import { toHash } from '@mono/to-hash';
import circle from '@turf/circle';
import { DateTime, Duration } from 'luxon';

const steps = 128;
const unitToTurfUnits = (unit: 'km' | 'nm' = 'km') => {
    if (unit === 'nm') return 'nauticalmiles';
    return 'kilometers';
};

export const generateLightningCircleGeojsons = (lightningCircle: LightningCircle, strikes: Strikes, selectedTime: string) => {
    const filteredStrikes = {
        ...strikes,
        features: strikes.features.filter((strike) => lightningCircleContainsStrike(lightningCircle, strike)).reverse(),
    };
    const { coordinates, sectors, unit } = lightningCircle;

    if (sectors < 2) {
        // We don't currently support one sector (ie a circle)
        // If we want to, this is where we should do it.
        return {
            segments: [],
            lines: {},
        };
    }

    const segments = lightningCircle.ringRadii
        .map((radius, radiusIndex) => {
            return Array.from({ length: sectors }).reduce<
                {
                    geojson: Feature<Polygon, Properties>;
                    firstInnerPoint: Position;
                    lastInnerPoint: Position;
                    firstOuterPoint: Position;
                    lastOuterPoint: Position;
                    bearing1: number;
                    bearing2: number;
                    innerRadius: number;
                    outerRadius: number;
                }[]
            >((segments, _, sectorIndex) => {
                const nextRadius = lightningCircle.ringRadii[radiusIndex + 1];
                if (!nextRadius) return segments;
                const angle = 360 / lightningCircle.sectors;
                const angleOffset = angle * sectorIndex + angle / 2;

                const innerArc = lineArc(coordinates, radius, angleOffset, angleOffset + angle, { steps, units: unitToTurfUnits(unit) }).geometry.coordinates;
                const previousSegment = segments[sectorIndex - 1];
                const firstInnerPoint = previousSegment ? previousSegment.lastInnerPoint : innerArc[0];
                const lastInnerPoint = innerArc[innerArc.length - 1];
                const outerArc = lineArc(coordinates, nextRadius, angleOffset, angleOffset + angle, { steps, units: unitToTurfUnits(unit) }).geometry
                    .coordinates;
                const firstOuterPoint = previousSegment ? previousSegment.lastOuterPoint : outerArc[0];
                const lastOuterPoint = outerArc[outerArc.length - 1];

                if (sectorIndex === sectors - 1) {
                    const lastInnerPoint = segments[0].firstInnerPoint;
                    const lastOuterPoint = segments[0].firstOuterPoint;
                    const geojson = polygon([
                        [
                            previousSegment.lastInnerPoint,
                            ...innerArc,
                            lastInnerPoint,
                            lastOuterPoint,
                            ...outerArc.reverse(),
                            previousSegment.lastOuterPoint,
                            previousSegment.lastInnerPoint,
                        ],
                    ]);
                    const status = getSegmentStatus(geojson, filteredStrikes, lightningCircle.warningAge, lightningCircle.alertAge, selectedTime);
                    return [
                        ...segments,
                        {
                            geojson: {
                                ...geojson,
                                properties: {
                                    ...geojson.properties,
                                    status,
                                },
                            },
                            firstInnerPoint,
                            lastInnerPoint,
                            firstOuterPoint,
                            lastOuterPoint,
                            bearing1: angleOffset,
                            bearing2: angleOffset + angle,
                            innerRadius: radius,
                            outerRadius: nextRadius,
                        },
                    ];
                }

                const geojson = previousSegment
                    ? polygon([
                          [previousSegment.lastInnerPoint, ...innerArc, ...outerArc.reverse(), previousSegment.lastOuterPoint, previousSegment.lastInnerPoint],
                      ])
                    : polygon([[...innerArc, lastOuterPoint, ...outerArc.reverse(), firstInnerPoint]]);

                const status = getSegmentStatus(geojson, filteredStrikes, lightningCircle.warningAge, lightningCircle.alertAge, selectedTime);
                return [
                    ...segments,
                    {
                        geojson: {
                            ...geojson,
                            properties: {
                                ...geojson.properties,
                                status,
                            },
                        },
                        firstInnerPoint,
                        lastInnerPoint,
                        firstOuterPoint,
                        lastOuterPoint,
                        bearing1: angleOffset,
                        bearing2: angleOffset + angle,
                        innerRadius: radius,
                        outerRadius: nextRadius,
                    },
                ];
            }, []);
        })
        .flat()
        .filter((x): x is NonNullable<typeof x> => x !== null);

    const innerCircle = circle(lightningCircle.coordinates, lightningCircle.ringRadii[0], { steps, units: unitToTurfUnits(lightningCircle.unit) });
    const innerCircleWithStatus = {
        ...innerCircle,
        properties: {
            ...innerCircle.properties,
            status: getSegmentStatus(innerCircle, filteredStrikes, lightningCircle.warningAge, lightningCircle.alertAge, selectedTime),
        },
    };

    const lines = segments.reduce<{ [lineId: string]: Feature<LineString, Properties> }>((lines, segment) => {
        const { firstInnerPoint, firstOuterPoint, lastInnerPoint, lastOuterPoint, geojson: segmentGeojson } = segment;
        const leftLineId = toHash([firstInnerPoint, firstOuterPoint]);
        const rightLineId = toHash([lastInnerPoint, lastOuterPoint]);
        const leftLineGeojson = lineString([firstInnerPoint, firstOuterPoint]);
        const rightLineGeojson = lineString([lastInnerPoint, lastOuterPoint]);

        const innerArcLineId = toHash([firstInnerPoint, lastInnerPoint]);
        const outerArcLineId = toHash([firstOuterPoint, lastOuterPoint]);

        const innerArcGeojson = lineArc(lightningCircle.coordinates, segment.innerRadius, segment.bearing1, segment.bearing2, {
            steps,
            units: unitToTurfUnits(lightningCircle.unit),
        });

        const outerArcGeojson = lineArc(lightningCircle.coordinates, segment.outerRadius, segment.bearing1, segment.bearing2, {
            steps,
            units: unitToTurfUnits(lightningCircle.unit),
        });

        return {
            ...lines,
            [leftLineId]: {
                ...leftLineGeojson,
                properties: {
                    ...leftLineGeojson.properties,
                    status: Math.max(segmentGeojson.properties?.status ?? 0, lines[leftLineId]?.properties?.status ?? 0),
                },
            },
            [rightLineId]: {
                ...rightLineGeojson,
                properties: {
                    ...rightLineGeojson.properties,
                    status: Math.max(segmentGeojson.properties?.status ?? 0, lines[rightLineId]?.properties?.status ?? 0),
                },
            },
            [innerArcLineId]: {
                ...innerArcGeojson,
                geometry: {
                    ...innerArcGeojson.geometry,
                    coordinates: [segment.firstInnerPoint, ...innerArcGeojson.geometry.coordinates, segment.lastInnerPoint],
                },
                properties: {
                    ...innerArcGeojson.properties,
                    status: Math.max(
                        segmentGeojson.properties?.status ?? 0,
                        lines[innerArcLineId]?.properties?.status ?? 0,
                        segment.innerRadius === lightningCircle.ringRadii[0] ? innerCircleWithStatus.properties?.status ?? 0 : 0
                    ),
                },
            },
            [outerArcLineId]: {
                ...outerArcGeojson,
                geometry: {
                    ...outerArcGeojson.geometry,
                    coordinates: [segment.firstOuterPoint, ...outerArcGeojson.geometry.coordinates, segment.lastOuterPoint],
                },
                properties: {
                    ...outerArcGeojson.properties,
                    status: Math.max(segmentGeojson.properties?.status ?? 0, lines[outerArcLineId]?.properties?.status ?? 0),
                },
            },
        };
    }, {});

    return {
        segments: [...segments.map((s) => s.geojson), innerCircleWithStatus],
        lines,
    };
};

const getSegmentStatus = (polygon: Feature<Polygon, Properties>, strikes: Strikes, warningAge: string, alertAge: string, selectedTime: string) => {
    const newestStrike = strikes.features.find((strike) => {
        return booleanPointInPolygon(point(strike.geometry.coordinates), polygon);
    });
    if (!newestStrike) return 0;

    const now = DateTime.fromISO(selectedTime).setZone('utc');

    const warningMinutes = Duration.fromISO(warningAge).as('minutes');
    const alertMinutes = Duration.fromISO(alertAge).as('minutes');
    if (now.toSeconds() - newestStrike.properties.unixTime < 60 * alertMinutes) return 2;
    if (now.toSeconds() - newestStrike.properties.unixTime < 60 * warningMinutes) return 1;
    return 0;
};

export const lightningCircleContainsStrike = (lightningCircle: LightningCircle, strike: Feature<Point, LightningFeatureProperties>) => {
    return geolib.isPointWithinRadius(
        {
            latitude: strike.geometry.coordinates[1],
            longitude: strike.geometry.coordinates[0],
        },
        {
            latitude: lightningCircle.coordinates[1],
            longitude: lightningCircle.coordinates[0],
        },
        // 1000 is meters to km, 1852 is meters to nautical miles
        lightningCircle.ringRadii[lightningCircle.ringRadii.length - 1] * (lightningCircle.unit === 'km' ? 1000 : 1852)
    );
};
